import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageTitle } from 'context/PageTitleContext'
import { listPendingPrintRequests, listCompletedPrintRequests, completePrintRequest } from 'services/clients.svc'
import { downloadQrCode } from 'common/functions'
import './QrPrintRequests.scss'

const getFullName = (detail) => {
  if (!detail) return 'Unknown'
  return [detail.firstName, detail.surname].filter(Boolean).join(' ')
}

const getSiteAddress = (site) => {
  if (!site) return '—'
  return [site.address, site.suburb, site.postcode].filter(Boolean).join(', ') || '—'
}

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const TABLE_HEADERS_PENDING = [
  'Company Name', 'Requested By', 'Phone Number', 'Office Address',
  'Site Address', 'Requested At', 'QR Code', 'Status',
]

const TABLE_HEADERS_COMPLETED = [
  'Company Name', 'Requested By', 'Phone Number', 'Office Address',
  'Site Address', 'Requested At', 'QR Code', 'Completed At', 'Completed By',
]

function QrCodeCell({ req }) {
  if (!req.qrCodeUrl) return '—'
  return (
    <button
      className="qr-download-btn"
      onClick={() => downloadQrCode(
        req.qrCodeUrl,
        req.projectSite?.clientName || req.company?.name,
        req.projectSite?.siteNumber,
      )}
    >
      Download
    </button>
  )
}

function RequestsTable({ requests, headers, renderActions }) {
  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {requests.map((req) => (
            <tr key={req._id}>
              <td>{req.company?.name || '—'}</td>
              <td>{getFullName(req.requesterDetail)}</td>
              <td>{req.requesterDetail?.phone || '—'}</td>
              <td>{req.company?.address || '—'}</td>
              <td>{getSiteAddress(req.projectSite)}</td>
              <td>{formatDate(req.createdAt)}</td>
              <td><QrCodeCell req={req} /></td>
              {renderActions(req)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CompletedAccordion({ requests, isLoading }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="completed-accordion">
      <div className="accordion-header" onClick={() => setOpen((v) => !v)}>
        <span className="chevron">{open ? '▼' : '▶'}</span>
        <span>Completed Requests</span>
        {requests.length > 0 && (
          <span className="completed-badge">{requests.length}</span>
        )}
      </div>
      {open && (
        isLoading ? (
          <article aria-busy="true">Loading, Please Wait</article>
        ) : requests.length === 0 ? (
          <p className="accordion-empty">No completed QR print requests.</p>
        ) : (
          <RequestsTable
            requests={requests}
            headers={TABLE_HEADERS_COMPLETED}
            renderActions={(req) => (
              <>
                <td>{formatDate(req.completedAt)}</td>
                <td>{getFullName(req.completerDetail)}</td>
              </>
            )}
          />
        )
      )}
    </div>
  )
}

export default function QrPrintRequests() {
  const queryClient = useQueryClient()

  const { data: requests = [], isLoading } = useQuery(
    ['qrPrintRequests'],
    listPendingPrintRequests,
  )

  const { data: completedRequests = [], isLoading: isLoadingCompleted } = useQuery(
    ['qrPrintRequestsCompleted'],
    listCompletedPrintRequests,
  )

  const completeMutation = useMutation(
    (requestId) => completePrintRequest(requestId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['qrPrintRequests'])
        queryClient.invalidateQueries(['qrPrintRequestsCount'])
        queryClient.invalidateQueries(['qrPrintRequestsCompleted'])
        queryClient.invalidateQueries(['pendingPrintRequest'])
      },
    },
  )

  return (
    <div className="qr-print-requests">
      <PageTitle title="QR Print Requests" />
      <h2>QR Print Requests</h2>
      {isLoading ? (
        <article aria-busy="true">Loading, Please Wait</article>
      ) : requests.length === 0 ? (
        <p>No pending QR print requests.</p>
      ) : (
        <RequestsTable
          requests={requests}
          headers={TABLE_HEADERS_PENDING}
          renderActions={(req) => (
            <td>
              <input
                type="checkbox"
                title="Mark as done"
                disabled={completeMutation.isLoading}
                onChange={() => completeMutation.mutate(req._id)}
              />
            </td>
          )}
        />
      )}
      <CompletedAccordion requests={completedRequests} isLoading={isLoadingCompleted} />
    </div>
  )
}
